import * as React from 'react';
import { FormattedMessage } from 'react-intl';

import { LoggedInUserReviewQuery } from 'packages/shared/definitions/types';

import {
  ModalModel,
  ModalContext,
  IModalProps,
  useBreakpointValue,
} from '@notino/react-styleguide';

import { useDrawer } from '@components/BottomSheet/DrawerProvider';
import productDetailMessages from '@containers/ProductDetailContainer/messages';
import {
  ReviewSourceTypes,
  IRating,
} from '@containers/ProductDetailContainer/model';
import { useProductDetailContext } from '@containers/ProductDetailContainer/ProductDetailContext';

import { shouldOpenReviewModal } from '../../utils/shouldOpenReviewModal';

import { ReviewsForm } from './ReviewsFormModalBody/loadable';
import { ReviewsSentSuccessModalBody } from './ReviewsSentSuccesModal';

interface IUseReviewModalOrDrawerParameters {
  ratings: IRating[];
  productCode: string;
  masterId: string;
  catalogMasterId: string;
  isAllReviewsPage?: boolean;
  loggedInUserReview?: LoggedInUserReviewQuery['loggedInUserReview'];
  refetchLoggedInUserReview: () => Promise<void>;
}

export const useReviewModalOrDrawer = ({
  ratings,
  productCode,
  masterId,
  catalogMasterId,
  isAllReviewsPage,
  loggedInUserReview,
  refetchLoggedInUserReview,
}: IUseReviewModalOrDrawerParameters) => {
  const { toggleModal, isShown } = ModalContext.useModalContext();
  const [success, setSuccess] = React.useState(false);
  const isDesktop = useBreakpointValue({ md: true });
  const { toggleDrawer, isDrawerOpen } = useDrawer();
  const { product } = useProductDetailContext();
  const drawerContentRef = React.useRef<HTMLDivElement>(null);

  const handleSuccess = React.useCallback(async () => {
    setSuccess(true);
    await refetchLoggedInUserReview();
  }, [refetchLoggedInUserReview]);

  React.useEffect(() => {
    // reset success when modal closed so user can edit review when opened again
    if (!isShown || !isDrawerOpen) {
      setSuccess(false);
    }
  }, [isShown, isDrawerOpen]);

  const DRAWER_OPTIONS = React.useMemo(() => {
    return {
      header: (
        <FormattedMessage
          {...productDetailMessages.reviewHeader}
          values={{
            name: `${product?.brand?.name ?? ''} ${product?.collection ?? ''}`,
          }}
        />
      ),
      initialFocusRef: drawerContentRef,
    };
  }, [product?.brand?.name, product?.collection]);

  const MODAL_OPTIONS: IModalProps = React.useMemo(() => {
    return {
      size: ModalModel.ModalSizes.default,
      type: ModalModel.ModalTypes.default,
      center: false,
      noBorders: true,
      disableCloseButton: true,
      withFocusTrap: true,
    };
  }, []);

  const getReviewsFormComponent = React.useCallback(
    (source: ReviewSourceTypes) => {
      return (
        <ReviewsForm
          catalogMasterId={catalogMasterId}
          masterId={masterId}
          ratings={ratings}
          source={source}
          productCode={productCode}
          onSuccess={handleSuccess}
          loggedInUserReview={loggedInUserReview}
          isAllReviewsPage={isAllReviewsPage}
        />
      );
    },
    [
      catalogMasterId,
      handleSuccess,
      loggedInUserReview,
      masterId,
      productCode,
      ratings,
      isAllReviewsPage,
    ]
  );

  React.useEffect(() => {
    // After submit the success screen controls the modal/drawer layout;
    // re-pushing the form here would reset it (e.g. bring the drawer header back).
    if (success) {
      return;
    }

    // Read directly from window.location (not React Router) because the
    // cleanup hook removes the param via history.replaceState, which does
    // not update React Router state. This is safe — it's inside useEffect.
    const reviewSource = shouldOpenReviewModal(window.location.search);

    if (reviewSource) {
      const modalOrDrawerContent = (
        <div ref={drawerContentRef} tabIndex={-1}>
          {getReviewsFormComponent(reviewSource)}
        </div>
      );
      isDesktop
        ? toggleModal(modalOrDrawerContent, MODAL_OPTIONS)
        : toggleDrawer(modalOrDrawerContent, DRAWER_OPTIONS);
    }
  }, [
    success,
    masterId,
    catalogMasterId,
    productCode,
    ratings,
    toggleModal,
    handleSuccess,
    loggedInUserReview,
    isDesktop,
    toggleDrawer,
    getReviewsFormComponent,
    DRAWER_OPTIONS,
    MODAL_OPTIONS,
  ]);

  return React.useCallback(() => {
    const modalOrDrawerContent = (
      <div ref={drawerContentRef} tabIndex={-1}>
        {success ? (
          <ReviewsSentSuccessModalBody />
        ) : (
          getReviewsFormComponent(ReviewSourceTypes.DIRECT)
        )}
      </div>
    );

    isDesktop
      ? toggleModal(modalOrDrawerContent, MODAL_OPTIONS)
      : toggleDrawer(modalOrDrawerContent, DRAWER_OPTIONS);
  }, [
    success,
    getReviewsFormComponent,
    isDesktop,
    toggleModal,
    MODAL_OPTIONS,
    toggleDrawer,
    DRAWER_OPTIONS,
  ]);
};
